import { apiClient, type ApiResponse, type RequestConfig } from '@shared/api/api-client';
import type { FlightInformationSummary } from '@jema-manager/shared';

type GetRequestConfig = Omit<RequestConfig, 'method' | 'body'>;

// Types matching backend
export type QuotingQueueStage =
    | 'quote_accepted'
    | 'collect_docs'
    | 'provider_confirmation'
    | 'trip_planning'
    | 'payment_deposit'
    | 'deposit_paid'
    | 'itinerary_ok'
    | 'pre_arrival'
    | 'ready_travel'
    | 'closed';

export interface QuotingQueueCard {
    id: string;
    currentStage: QuotingQueueStage;
    createdAt: string;
    updatedAt: string;
    priority: string;
    intake: {
        intakeFormId: string;
        referenceUid: string | null;
        formUid: string | null;
        specialty: string | null;
        patientEmail: string | null;
        patientName: string | null;
    };
    acceptedQuote: null | {
        id: string;
        doctorId: string;
        total: number;
        currency: string;
    };
    assignedDoctor: null | {
        doctorId: string;
        displayName: string | null;
        clinicName: string | null;
    };
    docs: {
        required: number;
        submitted: number;
    };
    flightInformationSummary?: FlightInformationSummary | null;

    travelCase: null | {
        id: string;
        planId: string | null;
        status: 'draft' | 'planned' | 'deposit_pending' | 'deposit_paid' | 'archived';
        mergedUsdTotal: number | null;
    };
}

export interface QuotingQueueTravelCaseGroup {
    travelCaseId: string;
    planId: string | null;
    status: 'draft' | 'planned' | 'deposit_pending' | 'deposit_paid' | 'archived';
    mergedUsdTotal: number | null;
    quoteCount: number;
    serviceRequestIds: string[];
}

export interface QuotingQueuePatientGroup {
    patientKey: string;
    patientName: string | null;
    patientEmail: string | null;
    serviceRequests: QuotingQueueCard[];
    travelCases?: QuotingQueueTravelCaseGroup[];
}

export interface PendingQuote {
    id: string;
    doctorId: string;
    doctorName: string | null;
    clinicName: string | null;
    total: number;
    currency: string;
    status: string;
    validUntil: string;
    createdAt: string;
    serviceRequestId: string;
}

export interface PatientServiceRequest {
    id: string;
    currentStage: string;
    specialty: string | null;
    referenceUid: string | null;
    createdAt: string;
    pendingQuotes: PendingQuote[];
}

export interface PatientDetails {
    patientKey: string;
    patientName: string | null;
    patientEmail: string | null;
    activeServiceRequests: PatientServiceRequest[];
}

export interface ServiceRequestDetails {
    serviceRequest: any;
    acceptedQuote: any | null;
    doctor: any | null;
    documentRequests: any[];
    documentSubmissions: any[];
}

export interface DocumentRequest {
    id: string;
    service_request_id: string;
    label: string;
    is_required: boolean;
    sort_order: number;
    created_at: string;
    updated_at: string;
}

// API Functions
export const quotingQueueApi = {
    /**
     * List all service requests grouped by patient for Kanban
     */
    list: async (params?: {
        stage?: QuotingQueueStage;
        patientEmail?: string;
        limit?: number;
    }, config?: GetRequestConfig): Promise<QuotingQueuePatientGroup[]> => {
        const response = await apiClient.get<ApiResponse<QuotingQueuePatientGroup[]>>(
            '/shared/quoting-queue',
            params,
            config,
        );
        return response.data;
    },

    /**
     * Get patient details with active SRs and pending quotes
     */
    getPatientDetails: async (patientKey: string, config?: GetRequestConfig): Promise<PatientDetails> => {
        const response = await apiClient.get<ApiResponse<PatientDetails>>(
            `/shared/quoting-queue/patients/${encodeURIComponent(patientKey)}`,
            undefined,
            config,
        );
        return response.data;
    },

    /**
     * Get full service request details
     */
    getServiceRequest: async (id: string, config?: GetRequestConfig): Promise<ServiceRequestDetails> => {
        const response = await apiClient.get<ApiResponse<ServiceRequestDetails>>(
            `/shared/quoting-queue/${id}`,
            undefined,
            config,
        );
        return response.data;
    },

    /**
     * Move service request to different stage
     */
    updateStage: async (
        id: string,
        stage: QuotingQueueStage
    ): Promise<{ id: string; currentStage: string }> => {
        const response = await apiClient.patch<ApiResponse<{ id: string; currentStage: string }>>(
            `/shared/quoting-queue/${id}/stage`,
            { stage }
        );
        return response.data;
    },

    /**
     * List document requests for a service request
     */
    listDocRequests: async (serviceRequestId: string, config?: GetRequestConfig): Promise<DocumentRequest[]> => {
        const response = await apiClient.get<ApiResponse<DocumentRequest[]>>(
            `/shared/quoting-queue/${serviceRequestId}/docs`,
            undefined,
            config,
        );
        return response.data;
    },

    /**
     * Create a document request
     */
    createDocRequest: async (
        serviceRequestId: string,
        payload: { label: string; is_required: boolean; sort_order?: number }
    ): Promise<DocumentRequest> => {
        const response = await apiClient.post<ApiResponse<DocumentRequest>>(
            `/shared/quoting-queue/${serviceRequestId}/docs`,
            payload
        );
        return response.data;
    },

    /**
     * Send docs collection link to patient
     */
    sendDocsLink: async (
        serviceRequestId: string
    ): Promise<{ token: string; url: string }> => {
        const response = await apiClient.post<ApiResponse<{ token: string; url: string }>>(
            `/shared/quoting-queue/${serviceRequestId}/docs/send-link`,
            {}
        );
        return response.data;
    },
};
